import type {AccessLogRecord} from '@/entities/log/types';
import type {SortAccessors, SortState} from '@/shared/lib/table-sort';

/**
 * Чем сортируется журнал — то есть какие столбцы у него есть и что в них лежит.
 *
 * Самих правил сортировки здесь нет: они общие на весь кабинет и живут в
 * [`shared/lib/table-sort`](../../../shared/lib/table-sort.ts) — пустое всегда внизу,
 * слова по правилам языка, порядок устойчив, у чужого столбца осмысленная сторона.
 * Раньше журнал держал собственную копию этих правил, и это была ровно та ситуация,
 * из-за которой в одной таблице «Ёлкин» стоял между «Евсеевым» и «Жуковым», а в
 * соседней — после «Яковлева».
 */
export type JournalSortKey =
  | 'time'
  | 'person'
  | 'number'
  | 'department'
  | 'device'
  | 'result'
  | 'late';

/** Журнал открывается новыми сверху: его читают от последнего события назад. */
export const DEFAULT_SORT: SortState = {key: 'time', direction: 'desc'};

/**
 * Минуты опоздания по строке. Живёт снаружи, потому что опоздание журналу не
 * принадлежит: его знает движок явки, а сюда оно приезжает уже сведённым
 * (`lib/lateness.ts`).
 */
export type LateResolver = (row: AccessLogRecord) => number | null;

export function journalAccessors(
  lateOf: LateResolver,
): SortAccessors<AccessLogRecord> {
  return {
    time: r => r.created_at_millis,
    person: r => r.person_name,
    number: r => r.employee_number,
    department: r => r.department_name,
    device: r => r.device_name,
    /**
     * «Насколько это отказ», а не «разрешён ли».
     *
     * Столбец «Результат» сортируют ради одного: собрать отказы наверх. Общее правило
     * открывает признак «от большего», так что первое нажатие обязано дать единицу —
     * то есть отказ. С прямым `granted` первое нажатие показывало бы разрешённые
     * проходы, которых и так большинство.
     */
    result: r => (r.granted ? 0 : 1),
    late: lateOf,
  };
}
